import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ContentSection,
  PatchAdminV1StoresStoreIdSectionsIdBody,
  PostAdminV1StoresStoreIdSectionsBody,
} from '@nucleus-commerce/api-client/admin';

import { sectionsApi } from '../api/content-api';

import { contentKeys, isAnyBlockList } from './content-keys';

function remember(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
  row: ContentSection,
) {
  queryClient.setQueryData(contentKeys.sectionDetail(storeId, row.id), row);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: contentKeys.sectionLists(storeId) }),
    // Blocks show how many sections place them.
    queryClient.invalidateQueries({ predicate: (query) => isAnyBlockList(query.queryKey) }),
  ]);
}

export function useCreateSection(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdSectionsBody) => sectionsApi.create(storeId, body),
    meta: { successMessage: 'content:toasts.sectionCreated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useUpdateSection(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdSectionsIdBody }) =>
      sectionsApi.update(storeId, id, body),
    meta: { successMessage: 'content:toasts.sectionUpdated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useDeleteSection(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sectionsApi.remove(storeId, id),
    meta: { successMessage: 'content:toasts.sectionDeleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: contentKeys.sectionDetail(storeId, id) });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.sectionLists(storeId) }),
        queryClient.invalidateQueries({ predicate: (query) => isAnyBlockList(query.queryKey) }),
      ]);
    },
  });
}

/** Replaces the ordered block list with one PUT; the section comes back with it applied. */
export function useSetSectionBlocks(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, blockIds }: { id: string; blockIds: string[] }) =>
      sectionsApi.setBlocks(storeId, id, blockIds),
    meta: { successMessage: 'content:toasts.sectionBlocksSaved', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}
